import React from "react";
import { Dna, Brain, Baby, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent, CardFooter, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const pathways = [{
  title: "Genetic Evaluation",
  description: "Cutting-edge genetic testing resulting in medication and treatment plans tailored to your biological profile.",
  icon: Dna,
  href: "https://sharmatpsychiatricgroup.com/precision-care/"
}, {
  title: "Adult Psychiatry",
  description: "Comprehensive care for complex adult cases, combining personalized medication management with evidence-based treatments.",
  icon: Brain,
  href: "/ClinicalLeadershipPage#dr--samuel-l--sharmat"
}, {
  title: "Pediatric Psychiatry",
  description: "Compassionate, science-driven support for children and adolescents, ensuring a foundation for lifelong mental wellness.",
  icon: Baby,
  href: "/ClinicalLeadershipPage#maryellen-stephens"
}];
export default function OurTreatmentPathways() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-treatment-pathways" ref={ref} className="relative w-full py-24 overflow-hidden bg-muted">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-ring/10 rounded-full blur-3xl -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl translate-y-1/2 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className={cn("max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-4xl md:text-5xl font-serif font-medium text-foreground mb-6">
            Our Treatment Pathways
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">Explore our specialized services designed to provide clarity and relief through precision medicine.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {pathways.map((pathway, i) => <div key={i} data-index={i} className={cn("transition-all duration-700 ease-out delay-[100ms]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
          transitionDelay: `${150 + i * 150}ms`
        }}>
              <Card className="h-full border border-white/40 bg-white/60 dark:bg-black/20 backdrop-blur-xl shadow-sm hover:shadow-[0_0_30px_-5px_var(--color-ring)] transition-all duration-500 group overflow-hidden flex flex-col">
                <CardHeader className="pb-4">
                  <div className="mb-6 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/50 text-ring shadow-sm group-hover:scale-110 group-hover:bg-ring group-hover:text-white transition-all duration-500">
                    <pathway.icon className="w-7 h-7" />
                  </div>
                  <CardTitle className="text-2xl font-medium text-foreground group-hover:text-ring transition-colors duration-300">
                    {pathway.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <CardDescription className="text-base text-muted-foreground/80 leading-relaxed">
                    {pathway.description}
                  </CardDescription>
                </CardContent>
                <CardFooter className="pt-4">
                  <Button asChild variant="ghost" className="group/btn pl-0 hover:bg-transparent hover:text-ring text-foreground font-medium text-base">
                    <Link to={pathway.href}>
                      Learn More
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}